/*
 * Copyright (c) 2020 NOOGA DEVELOPMENT, All rights reserved.
 * Author Raman Kremko <raman.kremko@noogadev.com>
 */

import { Epic, ofType } from 'redux-observable';
import {
    CashRegistersDetailsActionType,
    CashRegistersDetailsAction,
    fetchCashRegistersDetailsSuccessAction,
    fetchCashRegisterCardDataErrorAction,
    fetchVersionCheckSumsSuccessAction,
    startAttestationSuccessAction,
    completeAttestationSuccessAction,
    fetchSoftwareVersionsSuccessAction,
    activateModelSuccessAction,
    eliminateModelSuccessAction,
    activateVersionSuccessAction,
    markVersionOutdatedSuccessAction,
    fetchModelDocumentsSuccessAction,
    submitDocumentsSuccessAction,
    downloadDocumentSuccessAction,
    deleteDocumentsSuccessAction,
    deleteDocumentsErrorAction,
    updateModelDetailsSuccessAction,
    fetchFactoryNumbersListSuccessAction,
} from '../actions/cashRegistersDetailsActions';
import { catchError, map, switchMap, takeUntil } from 'rxjs/operators';
import { from, of } from 'rxjs';
import { cashRegistersClient } from '../api/cashRegisters/cashRegistersClient';
import { AxiosError } from 'axios';
import { NOTIFICATION_TYPES, Notification, downloadFileByUrl, parseError } from '../../utils/utils';
import { i18n } from '../translation/i18n';
import { FactoryNumbersListQueryParams } from '../api/cashRegisters/cashRegistersClient.typings';
import { CashRegisterDetailsState } from '../reducers/cashRegisersDetailsReducer';

export const fetchCashRegisterDetailsEpic: Epic<CashRegistersDetailsAction> = (action$, state) => {
    return action$.pipe(
        ofType(
            CashRegistersDetailsActionType.OPEN_REGISTRY_CASH_REGISTER_CARD_PAGE,
            CashRegistersDetailsActionType.START_ATTESTATION_SUCCESS,
            CashRegistersDetailsActionType.COMPLETE_ATTESTATION_SUCCESS,
            CashRegistersDetailsActionType.ACTIVATE_MODEL_SUCCESS,
            CashRegistersDetailsActionType.ELIMINATE_MODEL_SUCCESS,
            CashRegistersDetailsActionType.UPDATE_CASH_REGISTER_DETAILS_SUCCESS,
        ),
        switchMap(() => {
            const taxNumber = state.value.routerState.route.params.CashRegistersTaxNumber;

            return from(cashRegistersClient.fetchCashRegisterModel(taxNumber)).pipe(
                map((resp) => fetchCashRegistersDetailsSuccessAction(resp)),

                catchError((err: AxiosError) =>
                    of(
                        fetchCashRegisterCardDataErrorAction(
                            parseError(
                                err,
                                i18n.t('RegistryScreens.errors.cashRegisterModelNotFound', { searchText: taxNumber }),
                            ),
                        ),
                    ),
                ),
                takeUntil(action$.ofType(CashRegistersDetailsActionType.EXIT_REGISTRY_CASH_REGISTER_CARD_PAGE)),
            );
        }),
    );
};

export const updateModelEpic: Epic<CashRegistersDetailsAction> = (action$, state) => {
    return action$.pipe(
        ofType(CashRegistersDetailsActionType.UPDATE_CASH_REGISTER_DETAILS),
        switchMap((action) => {
            const taxNumber = state.value.routerState.route.params.CashRegistersTaxNumber;
            return from(cashRegistersClient.updateCashRegisterModel(taxNumber, action.payload)).pipe(
                map(() => updateModelDetailsSuccessAction()),

                catchError((err: AxiosError) =>
                    of(
                        fetchCashRegisterCardDataErrorAction(
                            parseError(err, i18n.t('RegistryScreens.errors.updateModelFailed')),
                        ),
                    ),
                ),
            );
        }),
    );
};

export const startAttestationEpic: Epic<CashRegistersDetailsAction> = (action$) => {
    return action$.pipe(
        ofType(CashRegistersDetailsActionType.START_ATTESTATION),
        switchMap((action) => {
            return from(cashRegistersClient.startAttestation(action.payload)).pipe(
                map(() => startAttestationSuccessAction()),

                catchError((err: AxiosError) =>
                    of(fetchCashRegisterCardDataErrorAction(parseError(err, i18n.t('errors.requestFailed')))),
                ),
            );
        }),
    );
};

export const completeAttestationEpic: Epic<CashRegistersDetailsAction> = (action$) => {
    return action$.pipe(
        ofType(CashRegistersDetailsActionType.COMPLETE_ATTESTATION),
        switchMap((action) => {
            return from(cashRegistersClient.completeAttestation(action.payload)).pipe(
                map((resp) => {
                    const notification: Notification = {
                        type: 'NOTIFICATION',
                        message: i18n.t('RegistryScreens.attestationCompleted'),
                    };
                    if (!resp.accepted) {
                        notification.type = 'ERROR';
                        const reason = i18n.exists(`cashRegister.attestationErrors.${resp.responseStatus}`)
                            ? `${i18n.t(`cashRegister.attestationErrors.${resp.responseStatus}`)}`
                            : i18n.t('errors.requestFailed');
                        notification.message = i18n.t('RegistryScreens.attestationNotCompleted', { reason });
                    }
                    return completeAttestationSuccessAction(notification);
                }),

                catchError((err: AxiosError) =>
                    of(
                        fetchCashRegisterCardDataErrorAction(
                            parseError(
                                err,
                                i18n.t('RegistryScreens.attestationNotCompleted', {
                                    reason: i18n.t('errors.requestFailed'),
                                }),
                            ),
                        ),
                    ),
                ),
            );
        }),
    );
};

export const fetchSoftwareVersionsListEpic: Epic<CashRegistersDetailsAction> = (action$, state$) => {
    return action$.pipe(
        ofType(
            CashRegistersDetailsActionType.FETCH_SOFTWARE_VERSIONS_PAGE_START,
            CashRegistersDetailsActionType.FETCH_SOFTWARE_VERSIONS_FULL_LIST_START,
            CashRegistersDetailsActionType.START_ATTESTATION_SUCCESS,
            CashRegistersDetailsActionType.COMPLETE_ATTESTATION_SUCCESS,
            CashRegistersDetailsActionType.ACTIVATE_VERSION_SUCCESS,
            CashRegistersDetailsActionType.MARK_VERSION_OUTDATED_SUCCESS,
        ),
        switchMap((action) => {
            const cashRegisterTaxNumber = state$.value.routerState.route.params.CashRegistersTaxNumber;
            let queryParams = {
                limit: state$.value.cashRegisterDetailsReducer.versionsPageParams.limit,
                offset: state$.value.cashRegisterDetailsReducer.versionsPageParams.offset,
            };
            if (action.type === CashRegistersDetailsActionType.FETCH_SOFTWARE_VERSIONS_FULL_LIST_START)
                queryParams = { limit: null, offset: null };

            return from(cashRegistersClient.getModelSoftwareVersions(cashRegisterTaxNumber, queryParams)).pipe(
                map((resp) => {
                    return fetchSoftwareVersionsSuccessAction(resp);
                }),
                catchError((err: AxiosError) =>
                    of(
                        fetchCashRegisterCardDataErrorAction(
                            parseError(err, i18n.t('RegistryScreens.errors.cashRegisterModelVersionsNotFound')),
                        ),
                    ),
                ),
                takeUntil(action$.ofType(CashRegistersDetailsActionType.EXIT_REGISTRY_CASH_REGISTER_CARD_PAGE)),
            );
        }),
    );
};

export const fetchVersionCheckSumsEpic: Epic<CashRegistersDetailsAction> = (action$, state$) => {
    return action$.pipe(
        ofType(CashRegistersDetailsActionType.FETCH_VERSION_CHECK_SUMS_START),
        switchMap((action) => {
            const cashRegisterTaxNumber = state$.value.routerState.route.params.CashRegistersTaxNumber;
            const { versionId, versionName, pageParams } = action.payload;

            return from(cashRegistersClient.getVersionCheckSums(cashRegisterTaxNumber, versionId, pageParams)).pipe(
                map((resp) => {
                    return fetchVersionCheckSumsSuccessAction({ versionId, checkSumsHistory: resp });
                }),
                catchError((err: AxiosError) =>
                    of(
                        fetchCashRegisterCardDataErrorAction(
                            parseError(
                                err,
                                i18n.t('RegistryScreens.errors.cashRegisterModelVersionCheckSumsNotFound', {
                                    versionName,
                                }),
                            ),
                        ),
                    ),
                ),
                takeUntil(action$.ofType(CashRegistersDetailsActionType.EXIT_REGISTRY_CASH_REGISTER_CARD_PAGE)),
            );
        }),
    );
};

export const activateModelEpic: Epic<CashRegistersDetailsAction> = (action$, state$) => {
    return action$.pipe(
        ofType(CashRegistersDetailsActionType.ACTIVATE_MODEL),
        switchMap((action) => {
            const cashRegisterTaxNumber = state$.value.routerState.route.params.CashRegistersTaxNumber;
            return from(cashRegistersClient.activateModel(cashRegisterTaxNumber, action.payload)).pipe(
                map(() => activateModelSuccessAction()),

                catchError((err: AxiosError) =>
                    of(fetchCashRegisterCardDataErrorAction(parseError(err, i18n.t('errors.requestFailed')))),
                ),
            );
        }),
    );
};

export const eliminateModelEpic: Epic<CashRegistersDetailsAction> = (action$, state$) => {
    return action$.pipe(
        ofType(CashRegistersDetailsActionType.ELIMINATE_MODEL),
        switchMap((action) => {
            const cashRegisterTaxNumber = state$.value.routerState.route.params.CashRegistersTaxNumber;
            return from(cashRegistersClient.eliminateModel(cashRegisterTaxNumber, action.payload)).pipe(
                map(() => eliminateModelSuccessAction()),

                catchError((err: AxiosError) =>
                    of(fetchCashRegisterCardDataErrorAction(parseError(err, i18n.t('errors.requestFailed')))),
                ),
            );
        }),
    );
};

export const activateVersionEpic: Epic<CashRegistersDetailsAction> = (action$, state$) => {
    return action$.pipe(
        ofType(CashRegistersDetailsActionType.ACTIVATE_VERSION),
        switchMap((action) => {
            const cashRegisterTaxNumber = state$.value.routerState.route.params.CashRegistersTaxNumber;
            return from(cashRegistersClient.activateVersion(cashRegisterTaxNumber, action.payload)).pipe(
                map(() => activateVersionSuccessAction()),

                catchError((err: AxiosError) =>
                    of(fetchCashRegisterCardDataErrorAction(parseError(err, i18n.t('errors.requestFailed')))),
                ),
            );
        }),
    );
};

export const markVersionOutdatedEpic: Epic<CashRegistersDetailsAction> = (action$, state$) => {
    return action$.pipe(
        ofType(CashRegistersDetailsActionType.MARK_VERSION_OUTDATED),
        switchMap((action) => {
            const cashRegisterTaxNumber = state$.value.routerState.route.params.CashRegistersTaxNumber;
            return from(cashRegistersClient.markVersionOutdated(cashRegisterTaxNumber, action.payload)).pipe(
                map(() => markVersionOutdatedSuccessAction()),

                catchError((err: AxiosError) =>
                    of(fetchCashRegisterCardDataErrorAction(parseError(err, i18n.t('errors.requestFailed')))),
                ),
            );
        }),
    );
};

export const fetchDocumentsListEpic: Epic<CashRegistersDetailsAction> = (action$, state$) => {
    return action$.pipe(
        ofType(
            CashRegistersDetailsActionType.FETCH_MODEL_DOCUMENTS_START,
            CashRegistersDetailsActionType.APPLY_DOCUMENTS_PAGING,
            CashRegistersDetailsActionType.APPLY_DOCUMENTS_SORTING,
            CashRegistersDetailsActionType.START_ATTESTATION_SUCCESS,
            CashRegistersDetailsActionType.SUBMIT_DOCUMENTS_SUCCESS,
            CashRegistersDetailsActionType.DELETE_DOCUMENTS_SUCCESS,
            CashRegistersDetailsActionType.DELETE_DOCUMENTS_ERROR, //some files might be deleted
        ),
        switchMap(() => {
            const cashRegisterTaxNumber = state$.value.routerState.route.params.CashRegistersTaxNumber;
            const queryParams = {
                limit: state$.value.cashRegisterDetailsReducer.docsPageParams.limit,
                offset: state$.value.cashRegisterDetailsReducer.docsPageParams.offset,
                sortType: state$.value.cashRegisterDetailsReducer.docsSortParams.sortType,
                sortOrder: state$.value.cashRegisterDetailsReducer.docsSortParams.sortOrder,
            };

            return from(cashRegistersClient.getModelDocuments(cashRegisterTaxNumber, queryParams)).pipe(
                map((resp) => {
                    return fetchModelDocumentsSuccessAction(resp);
                }),

                catchError((err: AxiosError) =>
                    of(
                        fetchCashRegisterCardDataErrorAction(
                            parseError(err, i18n.t('RegistryScreens.errors.cashRegisterModelDocumentsNotFound')),
                        ),
                    ),
                ),

                takeUntil(action$.ofType(CashRegistersDetailsActionType.EXIT_REGISTRY_CASH_REGISTER_CARD_PAGE)),
            );
        }),
    );
};

export const submitDocumentsEpic: Epic<CashRegistersDetailsAction> = (action$, state$) => {
    return action$.pipe(
        ofType(CashRegistersDetailsActionType.SUBMIT_DOCUMENTS_START),
        switchMap((action) => {
            const cashRegisterTaxNumber = state$.value.routerState.route.params.CashRegistersTaxNumber;
            return from(cashRegistersClient.submitDocuments(cashRegisterTaxNumber, action.payload)).pipe(
                map(() => submitDocumentsSuccessAction()),

                catchError((err: AxiosError) =>
                    of(fetchCashRegisterCardDataErrorAction(parseError(err, i18n.t('errors.requestFailed')))),
                ),
            );
        }),
    );
};

export const downloadDocumentEpic: Epic<CashRegistersDetailsAction> = (action$, state$) => {
    return action$.pipe(
        ofType(CashRegistersDetailsActionType.DOWNLOAD_DOCUMENT_START),
        switchMap((action) => {
            const cashRegisterTaxNumber = state$.value.routerState.route.params.CashRegistersTaxNumber;
            const fileName = action.payload.name;
            return from(cashRegistersClient.getDocumentUrl(cashRegisterTaxNumber, action.payload.id)).pipe(
                switchMap((url) => {
                    return from(downloadFileByUrl(fileName, url)).pipe(
                        map(() => downloadDocumentSuccessAction()),

                        catchError((err: AxiosError) =>
                            of(
                                fetchCashRegisterCardDataErrorAction(
                                    parseError(err, i18n.t('errors.failedUploadFileName', { fileName })),
                                ),
                            ),
                        ),
                    );
                }),
                catchError((err: AxiosError) =>
                    of(
                        fetchCashRegisterCardDataErrorAction(
                            parseError(err, i18n.t('errors.failedToGetUrl', { fileName })),
                        ),
                    ),
                ),
            );
        }),
    );
};

export const deleteDocumentsEpic: Epic<CashRegistersDetailsAction> = (action$, state$) => {
    return action$.pipe(
        ofType(CashRegistersDetailsActionType.DELETE_DOCUMENTS_START),
        switchMap((action) => {
            const cashRegisterTaxNumber = state$.value.routerState.route.params.CashRegistersTaxNumber;
            return from(cashRegistersClient.deleteDocuments(cashRegisterTaxNumber, action.payload)).pipe(
                map(() => {
                    return deleteDocumentsSuccessAction({
                        type: NOTIFICATION_TYPES[1],
                        message: i18n.t('app.filesDeleted'),
                    });
                }),
                catchError((err: AxiosError) =>
                    of(deleteDocumentsErrorAction(parseError(err, i18n.t('errors.failedToDeleteFiles')))),
                ),
            );
        }),
    );
};

export const fetchFactoryNumbersListEpic: Epic<CashRegistersDetailsAction> = (action$, state$) => {
    return action$.pipe(
        ofType(
            CashRegistersDetailsActionType.FETCH_FACTORY_NUM_START,
            CashRegistersDetailsActionType.CHANGE_FACTORY_NUM_FILTER,
            CashRegistersDetailsActionType.CHANGE_FACTORY_NUM_PAGING,
        ),
        switchMap(() => {
            const { CashRegistersTaxNumber } = state$.value.routerState.route.params;
            const queryParams = makeFactoryNumbersListQueryParamsFromState(state$.value.cashRegisterDetailsReducer);

            return from(cashRegistersClient.listCashRegistersFactoryNumbers(queryParams, CashRegistersTaxNumber)).pipe(
                map((resp) => fetchFactoryNumbersListSuccessAction(resp)),

                catchError((err: AxiosError) =>
                    of(
                        fetchCashRegisterCardDataErrorAction(
                            parseError(err, i18n.t('RegistryScreens.errors.failedLoadFactoryNumbers')),
                        ),
                    ),
                ),
                takeUntil(action$.ofType(CashRegistersDetailsActionType.EXIT_REGISTRY_CASH_REGISTER_CARD_PAGE)),
            );
        }),
    );
};

function makeFactoryNumbersListQueryParamsFromState(state: CashRegisterDetailsState): FactoryNumbersListQueryParams {
    return {
        offset: state.factoryNumbersListPageParams.offset,
        limit: state.factoryNumbersListPageParams.limit,
        factoryNumber: state.factoryNumbersListFilterParams.factoryNumber,
        used: state.factoryNumbersListFilterParams.used,
    };
}
